import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { usePlayer } from 'providers/player/PlayerContext';
import { useChannels } from 'providers/useChannels/useChannels';
import { Broadcast, ProgramDetails, RecordingDetails, TVSeriesDetailEpisode, Movie } from 'types/Asset';
import { getUniquePlayOptions } from 'utils/fnData';
import { isBroadcast, isProgramDetails, isTvSeriesEpisode } from 'utils/fnTypeGuards';
import { getPlayerPropsForUrl } from 'hooks/usePlayerRouter';

type PlayOptionInput = ProgramDetails | Broadcast | TVSeriesDetailEpisode | RecordingDetails | Movie;

const usePlayOptionsService = () => {
    /* const [playerPath, setPlayerPath] = useState<string>(null); */
    const [input, setInput] = useState<PlayOptionInput>(null);
    const [playOptionsData, setPlayOptionsData] = useState(null);

    const { pathname } = useLocation();
    const { isMini, updateMiniPlayerProps } = usePlayer();
    const navigate = useNavigate();
    /* const { getReplayWindowTimeFrame } = useAuth(); */
    const { channels } = useChannels();

    /* const onNoInternet = () => {
        setPlayOptionsData(input.playOptions);
    }; */

    /* const { error, response, loading, fetcher: load, reset: resetLoader } = useDataFetcher<ProgramDetails, string>(
        Api.fetchProgramDetails(id) null,
        onNoInternet
    ); */

    const openSelector = (inputAsset: PlayOptionInput) => {
        setInput(inputAsset);
    };

    const closeSelector = () => {
        setInput(null);
        setPlayOptionsData(null);
        /* setPlayerPath(null); */
        /* resetLoader(); */
    };

    const referenceEventId = () => {
        if (!input) return null;

        if (isProgramDetails(input)) {
            return input.id;
        }

        if (isBroadcast(input)) {
            return input.id;
        }

        if (isTvSeriesEpisode(input)) {
            return input.eventId;
        }

        return null;
    };

    /* const handleEpgPlayOptions = (program: EpgProgram, playPath: string) => {
         setPlayerPath(playPath);
         load(program.id);
    }; */

    const optionsHandler = (asset: PlayOptionInput, playPath: string, filter: (asset) => boolean = () => true) => {
        const filteredPlayOptions = getUniquePlayOptions(asset.listings, /* getReplayWindowTimeFrame() */ null, null).filter(filter);

        if (filteredPlayOptions.length === 0 || filteredPlayOptions.length === 1) {
            if (isMini) {
                const playerProps = getPlayerPropsForUrl(playPath);

                if (playerProps) {
                    updateMiniPlayerProps(playerProps);
                }
            } else {
                navigate(playPath);
            }
        } else {
            openSelector(asset);
        }
    };

    // close selector when the path is changing so the caller parent gets unmounted
    useEffect(() => closeSelector(), [pathname]);

    useEffect(() => {
        if (input?.listings?.length) {
            const listings = input.listings.map((listing) => {
                const channelData = channels?.find((channel) => channel.id === listing.channel_id);
                return channelData ? { ...listing, channel: channelData } : null;
            });

            setPlayOptionsData(listings);
        }
    }, [input]);

    /* useEffect(() => {
        if (error || response) {
            if (response && playerPath) {
                optionsHandler(response, playerPath);
            }

            resetLoader();

            if (error && playerPath) {
                navigate(playerPath);
            }
        }
    }, [error, response, playerPath]); */

    return {
        openSelector,
        closeSelector,
        referenceEventId,
        /* handleEpgPlayOptions, */
        optionsHandler,
        playOptionsData,
        /* fetching: loading,
        load,
        response, */
    };
};

export const PlayOptionsContext = createContext<ReturnType<typeof usePlayOptionsService>>(null);

export const PlayOptionsProvider = ({ children }) => {
    const service = usePlayOptionsService();
    return <PlayOptionsContext.Provider value={service}>{children}</PlayOptionsContext.Provider>;
};

export const usePlayOptions = () => useContext(PlayOptionsContext);

export const usePlayOptionHelper = () => {
    const { optionsHandler } = usePlayOptions();
    const mounted = useRef(true);

    useEffect(
        () => () => {
            mounted.current = false;
        },
        []
    );

    return useCallback(
        (asset: PlayOptionInput, playPath: string, filter?: (asset) => boolean) => {
            if (!mounted.current) return;

            if (asset) {
                try {
                    optionsHandler(asset, playPath, filter);
                } catch (error) {
                    console.error('Error in playOptionHelper:', error);
                }
            }
        },
        [optionsHandler]
    );
};
